import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Players from './components/Players';
import Commands from './components/Commands';
import Punishments from './components/Punishments';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ServerProvider } from './contexts/ServerContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PvpLogs from './components/PvpLogs';
import PlayerDetails from './components/PlayerDetails';
import ActiveBansTab from './components/ActiveBansTab';
import StaffTab from './components/StaffTab';
import Map3D from './components/Map3D';
import ObservationTab from './components/ObservationTab';
import Reports from './components/Reports';
import FogRawGLBackground from './components/FogRawGLBackground';
import IpStats from './components/IpStats';
import TagsManager from './components/TagsManager';

function PrivateRoute({ children }) {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!user.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Доступ запрещен</h1>
          <p className="text-gray-400 mb-6">У вас нет прав администратора</p>
          <button
            onClick={async () => { await logout(); navigate('/login'); }}
            className="px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
          >
            Выйти
          </button>
        </div>
      </div>
    );
  }
  
  return children;
}

// ErrorBoundary-компонент
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    // Можно логировать ошибку
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gmod-dark">
          <div className="text-3xl font-bold text-theme-primary mb-4">Произошла ошибка</div>
          <div className="text-gmod-white text-lg mb-2">{String(this.state.error)}</div>
          <button className="mt-4 px-4 py-2 bg-theme-primary text-gmod-white rounded" onClick={() => window.location.reload()}>Перезагрузить страницу</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ServerProvider>
        <AuthProvider>
          <ThemeProvider>
            <Router>
            <div className="App">
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#191919',
                    color: '#bcbcbc',
                    border: '1px solid var(--color-primary, #dc255a)'
                  },
                }}
              />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }>
                  <Route index element={<Navigate to="/dashboard" />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="players" element={<Players />} />
                  <Route path="players/:steamId" element={<PlayerDetails />} />
                  <Route path="commands" element={<Commands />} />
                  <Route path="punishments" element={<Punishments />} />
                  <Route path="pvplogs" element={<PvpLogs />} />
                  <Route path="pvp-logs" element={<PvpLogs />} />
                  <Route path="active-bans" element={<ActiveBansTab />} />
                  <Route path="staff" element={<StaffTab />} />
                  <Route path="map" element={<Map3D />} />
                  <Route path="observation" element={<ObservationTab />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="tags" element={<TagsManager />} />
                  <Route path="/ip-stats" element={<PrivateRoute><IpStats /></PrivateRoute>} />
                </Route>
              </Routes>
            </div>
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </ServerProvider>
    </ErrorBoundary>
  );
}

export default App; 